/*
 * Copyright (c) 2022-2023 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { Component, signal, computed, inject, OnInit } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { FabAppLauncherItem, FabUser } from '@fabric/components/common';
import { FabHeaderComponent } from '@fabric/components/header';
import { FabLibraryInfo } from '@fabric/components/header-actions';
import { FabThemeService } from '@fabric/components/theme';
import { AuthenticationService } from './core/authentication/authentication.service';
import { appLauncherItemMap } from './core/config/app-launcher-items';
import { EnvironmentService } from './core/environment/environment.service';
import { SettingsService } from './core/settings/settings.service';
import { UsageTrackingService } from './core/usage-tracking/usage-tracking.service';
import { UserService } from './core/user/user.service';
import { MatButtonModule } from '@angular/material/button';
import { CommonModule } from '@angular/common';
import { IconService } from './service/icon/icon.service';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    imports: [
        MatButtonModule,
        FabHeaderComponent,
        RouterOutlet,
        CommonModule
    ]
})
export class AppComponent implements OnInit{
  public readonly appName: string;
  public readonly appVersion: string;
  public readonly appVersionCodename: string;
  public readonly appLauncherItems: FabAppLauncherItem[];
  public readonly apiMap: Record<string, string>;
  public readonly buildNumber: string;
  public readonly environmentName: string;
  public readonly libraries: FabLibraryInfo[];
  public user: FabUser = inject(UserService).user ?? ({} as FabUser);
  modelName = signal<string>('');
  isModelNamePopulated = computed(() => this.modelName() !== '');

  /**
   * Get the user authorities from the user service
   * @returns array of user authorities
   */
  protected get userAuthorities(): string[] {
    return this.userService.getAuthorities();
  }

  private readonly settingsService: SettingsService = inject(SettingsService);
  private readonly userService: UserService = inject(UserService);

  constructor(
    private readonly router: Router,
    private readonly iconService: IconService
  ) {

    const themeService: FabThemeService = inject(FabThemeService);
    const usageTrackingService: UsageTrackingService = inject(UsageTrackingService);

    // Enable Auth Silent Renew
    inject(AuthenticationService).enableSilentRenew();

    // Setup app info config.
    const environment = inject(EnvironmentService).environment;
    this.appName = environment.appName ?? 'ADD APP NAME';
    this.appVersion = environment.appVersion ?? '';
    this.appVersionCodename = environment.appVersionCodename ?? '';
    this.apiMap = environment.apiMap ?? {};
    this.buildNumber = environment.buildNumber as string;
    this.environmentName = environment.environmentName ?? '';
    this.libraries = environment.libraries ?? [];

    // Setup App Launcher config
    const urlMap = environment.appLauncherUrls;
    appLauncherItemMap.repo.linkUrl = urlMap?.repo ?? '';
    appLauncherItemMap.backlog.linkUrl = urlMap?.backlog ?? '';
    appLauncherItemMap.discussion.linkUrl = urlMap?.discussion ?? '';
    this.appLauncherItems = [appLauncherItemMap.repo, appLauncherItemMap.backlog, appLauncherItemMap.discussion];

    // Web usage analytics
    usageTrackingService.start({
      applicationName: this.appName,
      asmsId: 202995,
      asmsWebsiteId: 202995,
      environment: environment.environmentName ?? '',
      gmin: this.user?.gmin ?? '',
      uiFramework: 'Fabric',
    });

    // Apply theme setting from user settings
    themeService.themeSetting = this.settingsService.themeSetting;
    themeService.themeChange.subscribe((themeSetting) => {
      this.settingsService.themeSetting = themeSetting;
    });
  }

  ngOnInit(): void {
    this.iconService.registerIcons();
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        const currentRoute = event.urlAfterRedirects;
        const regex = /\/model\/([^/]+)/;
        const match = regex.exec(currentRoute); // Regex to extract model_name
        if (match) {
          this.modelName.set(match[1]);
        } else {
          this.modelName.set('');
        }
      }
    });
  }

  goHome(): void {
    this.router.navigate(['/']);
  }
}
